import { AuthService, FormPicklistService, CreateApplicationResult } from '@recruiting-solutions/candidate-ui-shared';
import { CsbHelper, Job, baseStore, loadingHelper } from '@recruiting-solutions/rs-shared';
import { Application, CandidateUser, Profile, UploadResult } from '../models';
import { ApplyService } from './apply.service';
import { FormUploadService } from './form-upload.service';
import { FormService } from './form.service';

export abstract class FormPageService {
    public saveErrorMsg: string | undefined;

    public formService: FormService;
    public formPicklistService: FormPicklistService;
    public formUploadService: FormUploadService;

    public fields?: string[] = undefined;

    public ignoreUploadValues?: string[] = undefined;

    constructor(host: HTMLElement | string, private loadingPostfix: string) {
        this.formService = new FormService(host);
        this.formPicklistService = new FormPicklistService(this.formService);
        this.formUploadService = new FormUploadService(this.formService);
    }

    getLoadingId(type?: string, postfix?: string) {
        return type && type + '-' + (postfix || this.loadingPostfix);
    }

    setLoading(type?: string, postfix?: string) {
        return loadingHelper.setLoading(this.getLoadingId(type, postfix));
    }

    fixLoading(type: string, postfix?: string) {
        loadingHelper.fixLoading(this.getLoadingId(type, postfix)!);
    }

    async init(additionalPicklistIds?: string[]) {
        const loading = this.setLoading('init');

        if (!additionalPicklistIds) {
            additionalPicklistIds = [];
        }

        let locale = CsbHelper.getLocale();
        let pickListData = await this.formPicklistService.loadPicklistData(locale, additionalPicklistIds);
        baseStore.state.pickListData = pickListData;

        // We might as well start initalising the field prop.
        this.initFields();

        // Show loading while waiting for possible user data
        setTimeout(() => {
            loading.off();
        }, 800);
    }

    async fill(data: any) {
        this.formService.loadObject(data);
        this.formUploadService.loadObject(data, this.ignoreUploadValues);
    }

    protected async initFields() {
        if (this.fields && this.fields.length) return;
        let candidateFields = await ApplyService.getCandidateFields();
        let applicationFields = await ApplyService.getApplicationFields();
        baseStore.state.fields = this.fields = [...candidateFields, ...applicationFields].map(f => f?.name);
    }

    abstract saveAjax(data: any): Promise<any>;

    async saveFieldsAndUpload(dataDecorator?: (data: any) => any): Promise<CreateApplicationResult> {
        const loading = this.setLoading('save');
        await AuthService.createOrGetTempUser();
        let result: CreateApplicationResult = false;
        let data = await this.formService.retrieveObject(this.fields || []);
        if (dataDecorator) {
            data = dataDecorator(data);
        }
        const uploadResults = await this.formUploadService.uploadFiles();

        let uploadSuccessfull = uploadResults.every(x => x !== UploadResult.Failed);

        try {
            if (data && uploadSuccessfull) {
                data = {
                    ...data,
                    filesToDelete: baseStore.state.filesToDelete,
                };

                let ajaxResult = await this.saveAjax(data);
                if (!ajaxResult) throw 'Could not save files and upload';

                baseStore.state.filesToDelete = [];
                result = ajaxResult;
            }
        } catch (error) {
            console.error('Error on save', error);
            this.onSaveError(error);
        } finally {
            loading.off();
        }
        return result;
    }

    onSaveError(_error: any) {
        baseStore.state.error = this.saveErrorMsg;
    }

    protected async saveProfileAjax(data: any) {
        data = {
            ...data,
            filesToDelete: baseStore.state.filesToDelete,
        };
        console.log('Save profile data:', data);

        let result = await ApplyService.updateProfile(data);
        if (!result) throw 'Could not update profile';

        baseStore.state.filesToDelete = [];
    }

    async loadProfile(): Promise<Profile> {
        const loading = this.setLoading('load');
        const profile = await ApplyService.getProfile();
        baseStore.state.profile = profile;
        console.log('Profile:', profile);
        loading.off();
        return profile;
    }

    async fillEmail(user: CandidateUser) {
        if (user.email) {
            this.formService.loadObjectOnlyWhenEmpty({
                contactEmail: user.email,
            });
        }
    }

    getFirstQuestionContainer(): any {
        let query = ['csb-field-questions-container', '[csb-field-questions-container]'];
        let all = this.formService.querySelectorAll(query);
        return all && all[0];
    }

    async getQuestionsFormData() {
        let container = this.getFirstQuestionContainer();
        return container ? await container.getQuestionFieldData() : [];
    }

    initQuestionContainer(job: Job, application?: Application) {
        if (!job) return;
        let qcontainer = this.getFirstQuestionContainer();
        if (qcontainer && !qcontainer.job) {
            qcontainer.job = job;
            if (application) qcontainer.answers = application.questions;
            qcontainer.formService = this.formService;
            qcontainer.initialize();
        }
    }
}
